import { useMemo } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { PowerBIEmbed, type EventHandler } from 'powerbi-client-react';
import { models } from 'powerbi-client';

import { Section, SectionCard, SectionHeader } from '@/components/common';
import { useUpwardFeedbackEmbed } from '@/queries/useAppraisalDetail';

interface UpwardFeedbackSectionProps {
  appraisalId: number;
  appraiseeBambooId: number;
}

const EMBED_HEIGHT = 600;

const EMBED_EVENT_HANDLERS = new Map<string, EventHandler>([
  ['error', (event) => console.error('[PowerBI embed]', event?.detail)],
]);

export function UpwardFeedbackSection({ appraisalId, appraiseeBambooId }: UpwardFeedbackSectionProps) {
  const { data: embed, isLoading, isError } = useUpwardFeedbackEmbed(appraisalId);

  // Report-level filter restricting the report to this appraisee's rows.
  const embedConfig = useMemo(() => {
    if (!embed) return undefined;

    const staffFilter: models.IBasicFilter = {
      $schema: 'http://powerbi.com/product/schema#basic',
      target: { table: embed.filterTable, column: embed.filterColumn },
      operator: 'In',
      values: [appraiseeBambooId],
      filterType: models.FilterType.Basic,
    };

    return {
      type: 'report',
      id: embed.reportId,
      embedUrl: embed.embedUrl,
      accessToken: embed.embedToken,
      tokenType: models.TokenType.Embed,
      filters: [staffFilter],
      settings: {
        filterPaneEnabled: false,
        panes: {
          filters: { visible: false },
          pageNavigation: { visible: false },
        },
      },
    } satisfies models.IReportEmbedConfiguration;
  }, [embed, appraiseeBambooId]);

  // The API answers 204 when the embed is switched off for this environment, which
  // reaches us as a null config. Nothing to show, and nothing has gone wrong.
  if (!isLoading && !isError && !embed) {
    return null;
  }

  return (
    <Section>
      <SectionHeader>UPWARD FEEDBACK</SectionHeader>

      <SectionCard>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: EMBED_HEIGHT }}>
            <CircularProgress />
          </Box>
        )}

        {isError && (
          <Alert severity="error">Unable to load the Upward Feedback report. Please try again later.</Alert>
        )}

        {embedConfig && (
          <Box
            sx={{
              height: EMBED_HEIGHT,
              '& .pbi-embed': { height: '100%' },
              '& iframe': { border: 'none', display: 'block' },
            }}
          >
            <PowerBIEmbed
              embedConfig={embedConfig}
              cssClassName="pbi-embed"
              getEmbeddedComponent={(embedded) => embedded.setComponentTitle('DWH Upward Feedback History')}
              eventHandlers={EMBED_EVENT_HANDLERS}
            />
          </Box>
        )}
      </SectionCard>
    </Section>
  );
}
